import { useTranslation } from "react-i18next";
import {
  LayoutDashboard,
  Users,
  ShoppingCart,
  Receipt,
  Boxes,
  UserCog,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";
import { SectionHeading } from "./SectionHeading";

const CARD =
  "group relative overflow-hidden rounded-3xl bg-white p-5 ring-1 ring-slate-200 shadow-sm transition-all hover:-translate-y-0.5 hover:ring-slate-300 dark:bg-white/[0.02] dark:ring-white/10 dark:shadow-none dark:hover:ring-white/20 sm:p-6";

function CardHeader({ icon: Icon, title }: { icon: LucideIcon; title: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-blue-50 text-blue-600 ring-1 ring-blue-100 dark:bg-white/5 dark:text-blue-300 dark:ring-white/10">
        <Icon size={15} />
      </span>
      <h3 className="font-semibold text-slate-900 dark:text-white">{title}</h3>
    </div>
  );
}

const BODY = "mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-400";

/** Bento-grid features section — mixed-size cards, each with a small
 * product-flavored visual. Theme-aware (light/dark). Cards render statically:
 * the old scroll-triggered reveals held below-the-fold content invisible for
 * seconds on iPhone Safari (see SESSIONS 2026-06-12). */
export function Capabilities() {
  const { t } = useTranslation();

  return (
    <section
      id="capabilities"
      className="relative scroll-mt-16 border-t border-slate-200/80 bg-white py-20 dark:border-white/5 dark:bg-slate-950 sm:py-28"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          tag={t("landing.capabilities.tag")}
          title={t("landing.capabilities.title")}
          subtitle={t("landing.capabilities.subtitle")}
        />

        <div className="grid auto-rows-[minmax(10rem,auto)] grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* Dashboard — large 2×2 cell with sparkline */}
          <div className={`${CARD} sm:col-span-2 lg:row-span-2`}>
            <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full glow [--glow:theme(colors.blue.400/10%)] dark:[--glow:theme(colors.blue.500/10%)] opacity-70 transition-opacity group-hover:opacity-100" />
            <CardHeader icon={LayoutDashboard} title={t("landing.capabilities.items.dashboard.title")} />
            <p className={BODY}>{t("landing.capabilities.items.dashboard.body")}</p>
            <div className="mt-5 rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200 dark:bg-slate-900/60 dark:ring-white/5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  {t("landing.demo.labels.revenueTrend")}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                  <TrendingUp size={10} />
                  +18.2%
                </span>
              </div>
              <svg viewBox="0 0 280 90" className="mt-2 h-28 w-full" preserveAspectRatio="none" aria-hidden>
                <defs>
                  <linearGradient id="bentoSpark" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="var(--accent-500)" stopOpacity="0.35" />
                    <stop offset="100%" stopColor="var(--accent-500)" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path
                  d="M0,70 C30,62 45,48 70,52 C95,56 110,38 140,40 C170,42 185,24 215,26 C240,28 260,14 280,10 L280,90 L0,90 Z"
                  fill="url(#bentoSpark)"
                />
                <path
                  d="M0,70 C30,62 45,48 70,52 C95,56 110,38 140,40 C170,42 185,24 215,26 C240,28 260,14 280,10"
                  fill="none"
                  stroke="var(--accent-400)"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>

          {/* Sales — wide cell with pay-status pills */}
          <div className={`${CARD} sm:col-span-2`}>
            <CardHeader icon={ShoppingCart} title={t("landing.capabilities.items.sales.title")} />
            <p className={BODY}>{t("landing.capabilities.items.sales.body")}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-500/30">
                ● {t("landing.demo.labels.paid")}
              </span>
              <span className="rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-700 ring-1 ring-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:ring-blue-500/30">
                ◐ {t("landing.demo.labels.partial")}
              </span>
              <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700 ring-1 ring-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-500/30">
                ○ {t("landing.demo.labels.unpaid")}
              </span>
            </div>
          </div>

          {/* Customers — avatar stack */}
          <div className={CARD}>
            <CardHeader icon={Users} title={t("landing.capabilities.items.customers.title")} />
            <p className={BODY}>{t("landing.capabilities.items.customers.body")}</p>
            <div className="mt-4 flex items-center">
              {["ML", "CR", "AT"].map((ini, i) => (
                <span
                  key={ini}
                  className={`flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 text-[9px] font-bold text-blue-700 ring-2 ring-white dark:from-blue-500/30 dark:to-indigo-500/30 dark:text-blue-100 dark:ring-slate-950 ${
                    i > 0 ? "-ml-2" : ""
                  }`}
                >
                  {ini}
                </span>
              ))}
              <span className="-ml-2 flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-[9px] font-bold text-slate-600 ring-2 ring-white dark:bg-white/10 dark:text-slate-300 dark:ring-slate-950">
                +38
              </span>
            </div>
          </div>

          {/* Inventory — stock bars */}
          <div className={CARD}>
            <CardHeader icon={Boxes} title={t("landing.capabilities.items.inventory.title")} />
            <p className={BODY}>{t("landing.capabilities.items.inventory.body")}</p>
            <div className="mt-4 space-y-1.5">
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-white/5">
                <div className="h-full w-4/5 rounded-full bg-blue-500/70 dark:bg-blue-400/70" />
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-white/5">
                <div className="h-full w-3/5 rounded-full bg-blue-500/50 dark:bg-blue-400/50" />
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-white/5">
                <div className="h-full w-1/5 rounded-full bg-amber-500/80 dark:bg-amber-400/80" />
              </div>
            </div>
          </div>

          {/* Expenses — mini ledger rows */}
          <div className={`${CARD} sm:col-span-1 lg:col-span-2`}>
            <CardHeader icon={Receipt} title={t("landing.capabilities.items.expenses.title")} />
            <p className={BODY}>{t("landing.capabilities.items.expenses.body")}</p>
            <div className="mt-4 space-y-1.5 text-[11px]">
              <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5 ring-1 ring-slate-200 dark:bg-slate-900/60 dark:ring-white/5">
                <span className="text-slate-500 dark:text-slate-400">{t("landing.bento.rent")}</span>
                <span className="font-semibold tabular-nums text-rose-600 dark:text-rose-300">−$1,200</span>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5 ring-1 ring-slate-200 dark:bg-slate-900/60 dark:ring-white/5">
                <span className="text-slate-500 dark:text-slate-400">{t("landing.bento.supplies")}</span>
                <span className="font-semibold tabular-nums text-rose-600 dark:text-rose-300">−$486</span>
              </div>
            </div>
          </div>

          {/* Team & goals — progress ring + role chips */}
          <div className={`${CARD} sm:col-span-1 lg:col-span-2`}>
            <CardHeader icon={UserCog} title={t("landing.capabilities.items.team.title")} />
            <p className={BODY}>{t("landing.capabilities.items.team.body")}</p>
            <div className="mt-4 flex items-center gap-4">
              <span className="relative flex h-12 w-12 flex-none items-center justify-center">
                <svg viewBox="0 0 40 40" className="h-12 w-12 -rotate-90" aria-hidden>
                  <circle
                    cx="20"
                    cy="20"
                    r="16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="4"
                    className="text-slate-200 dark:text-white/10"
                  />
                  <circle
                    cx="20"
                    cy="20"
                    r="16"
                    fill="none"
                    stroke="var(--accent-400)"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeDasharray="100.5"
                    strokeDashoffset="28"
                  />
                </svg>
                <span className="absolute text-[9px] font-bold text-slate-900 dark:text-white">72%</span>
              </span>
              <div className="flex flex-wrap gap-1.5">
                {(["goal", "admin", "sales"] as const).map((key) => (
                  <span
                    key={key}
                    className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-semibold text-slate-600 ring-1 ring-slate-200 dark:bg-white/5 dark:text-slate-300 dark:ring-white/10"
                  >
                    {t(`landing.bento.${key}`)}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
